/* DealDecisionPanel.jsx — Phase 30 per-deal decision-support panel.
 *
 * Mounted on Deal Room intel rail. Surfaces:
 *  • Recommended next operational move (action + suggested owner)
 *  • Resource priority band (P0..P3)
 *  • Leverage score + effort score
 *  • Recoverable £ + weekly cost of delay
 *  • Director-attention triggers (when applicable)
 */
import React, { useCallback, useEffect, useState } from "react";
import { Compass, Crown, AlertOctagon, Coins } from "lucide-react";
import { api } from "../../lib/api";

const POLL_MS = 120_000;

const fmtGBP = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `£${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `£${Math.round(v / 1_000)}K`;
  return `£${Math.round(v).toLocaleString()}`;
};

const BAND_TONE = {
  "P0 — act now":      { bg: "rgba(244,63,94,0.10)",  fg: "#9F1239" },
  "P1 — today":        { bg: "rgba(245,158,11,0.10)", fg: "#92400E" },
  "P2 — this week":    { bg: "rgba(99,102,241,0.08)", fg: "#4338CA" },
  "P3 — monitor":      { bg: "rgba(16,185,129,0.10)", fg: "#047857" },
  "Director immediate":{ bg: "rgba(244,63,94,0.10)",  fg: "#9F1239" },
  "Director today":    { bg: "rgba(245,158,11,0.10)", fg: "#92400E" },
  "Director this week":{ bg: "rgba(99,102,241,0.08)", fg: "#4338CA" },
  "Calm":              { bg: "rgba(16,185,129,0.10)", fg: "#047857" },
};

export function DealDecisionPanel({ dealId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    if (!dealId) return;
    try {
      const res = await api.get(`/api/decisions/deals/${dealId}`);
      setData(res.data?.decision || null);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => { refetch(); }, [refetch]);
  useEffect(() => {
    const tick = () => { if (document.visibilityState === "visible") refetch(); };
    const id = setInterval(tick, POLL_MS);
    document.addEventListener("visibilitychange", tick);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [refetch]);

  if (loading || !data || !data.active) return null;

  const action = data.recommended_action || {};
  const priority = data.resource_priority || {};
  const leverage = data.leverage || {};
  const recovery = data.recovery_value || {};
  const attention = data.attention || {};
  const priorityTone = BAND_TONE[priority.band] || BAND_TONE["P2 — this week"];
  const attentionTone = BAND_TONE[attention.band] || BAND_TONE["Calm"];

  return (
    <section className="gp-card overflow-hidden"
             data-testid="dealroom-decision-panel">
      <header className="p-4 sm:p-5">
        <div className="flex items-center gap-1.5">
          <Compass size={11} className="text-violet-600" strokeWidth={2.3} />
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-violet-700">
            Operational Decision
          </p>
        </div>
        <h2 className="text-[15px] sm:text-[16px] font-semibold text-slate-900 mt-1 leading-tight"
            data-testid="dealroom-decision-narrative">
          {data.narrative}
        </h2>
        <div className="flex items-center flex-wrap gap-1.5 mt-2">
          <span className="text-[10.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md"
                style={{ background: priorityTone.bg, color: priorityTone.fg }}
                data-testid="dealroom-decision-priority-band">
            {priority.band}
          </span>
          <span className="text-[10.5px] tabular-nums px-2 py-0.5 rounded-md"
                style={{ background: "rgba(99,102,241,0.06)", color: "#4338CA" }}>
            Leverage {leverage.score}/100 · effort {leverage.effort_score}/10
          </span>
          <span className="text-[10.5px] px-2 py-0.5 rounded-md"
                style={{ background: "rgba(16,185,129,0.08)", color: "#047857" }}>
            <Coins size={9} className="inline -mt-0.5 mr-0.5" />
            {fmtGBP(recovery.recoverable_gbp)} recoverable
          </span>
        </div>
      </header>
      <div className="px-4 sm:px-5 pb-3 pt-2 border-t"
           style={{ borderColor: "var(--gp-card-border)" }}
           data-testid="dealroom-decision-recommendation">
        <p className="text-[9.5px] uppercase tracking-[0.18em] font-bold"
           style={{ color: "var(--gp-violet, #7C3AED)" }}>
          Recommended next operational move
        </p>
        <div className="flex items-start gap-2 mt-1.5 rounded-md p-2"
             style={{ background: "rgba(124,58,237,0.04)",
                      border: "1px solid rgba(124,58,237,0.10)" }}>
          <Crown size={11} className="text-violet-600 shrink-0 mt-0.5" strokeWidth={2.3} />
          <div className="min-w-0">
            <p className="text-[12px] font-semibold text-slate-900">
              {(action.action_kind || "").replace(/_/g, " ") || "—"}
            </p>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Suggested owner · {action.suggested_owner}
            </p>
          </div>
        </div>
        <p className="text-[10.5px] text-slate-500 mt-2">
          Weekly cost of delay · {fmtGBP(recovery.weekly_cost_of_delay_gbp)} eroded per week without intervention.
        </p>
      </div>
      {(attention.triggers || []).length ? (
        <div className="px-4 sm:px-5 pb-4 pt-2 border-t"
             style={{ borderColor: "var(--gp-card-border)" }}
             data-testid="dealroom-decision-attention">
          <div className="flex items-center gap-1.5 mb-1.5">
            <AlertOctagon size={11} strokeWidth={2.3} style={{ color: "var(--gp-violet, #7C3AED)" }} />
            <p className="text-[9.5px] uppercase tracking-[0.18em] font-bold"
               style={{ color: "var(--gp-violet, #7C3AED)" }}>
              Why this action matters now
            </p>
            <span className="text-[10.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md ml-auto"
                  style={{ background: attentionTone.bg, color: attentionTone.fg }}>
              {attention.band}
            </span>
          </div>
          <ul data-testid="dealroom-decision-triggers">
            {attention.triggers.map((t, i) => (
              <li key={i} className="flex items-start gap-2 py-1"
                  data-testid={`dealroom-decision-trigger-${t.trigger.replace(/_/g, "-")}`}>
                <span className="inline-block w-1.5 h-1.5 rounded-full mt-1.5 shrink-0"
                      style={{ background: "#F43F5E" }} />
                <p className="text-[11.5px] text-slate-800 leading-snug">{t.narrative}</p>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

export default DealDecisionPanel;
